import { criterion, fromCriteria, unsupported } from "@/core/check";
import type { Lesson } from "@/core/types";
import { readContentControls } from "@/office/word/formulare";
import { readFields } from "@/office/word/layout";
import { readEigenschaften } from "@/office/word/pruefen";

export const lektionEigenschaften: Lesson = {
  id: "eigenschaften",
  title: "Dokumenteigenschaften",
  summary: "Titel, Autor und Stichwörter – unsichtbar im Dokument, sichtbar für Suche, PDF und Screenreader.",
  durationMinutes: 7,
  learningGoals: [
    "Titel, Thema, Autor und Stichwörter pflegen",
    "Eine Dokumenteigenschaft als Feld im Text anzeigen",
  ],
  steps: [
    {
      id: "eigenschaften-pflegen",
      title: "Eigenschaften pflegen",
      explanation: [
        "Unter „Datei“ → „Informationen“ stehen rechts die Eigenschaften des Dokuments. " +
          "„Alle Eigenschaften anzeigen“ blendet weitere Felder ein. Der Titel ist dabei die " +
          "wichtigste Angabe: Ein PDF zeigt ihn in der Titelleiste statt des Dateinamens, " +
          "Screenreader lesen ihn beim Öffnen vor, und die Barrierefreiheitsprüfung mahnt " +
          "ihn an.",
        "Stichwörter und Thema helfen beim Wiederfinden in SharePoint und der Windows-Suche. " +
          "Der Autor wird aus dem Benutzerkonto übernommen – bei Dokumenten aus einer " +
          "Vorlage steht dort oft der Name der Person, die die Vorlage einmal gebaut hat.",
      ],
      keyPoints: [
        "Datei → Informationen → Eigenschaften.",
        "Der Titel erscheint im PDF und wird vorgelesen.",
        "Autor prüfen, wenn das Dokument aus einer Vorlage stammt.",
      ],
      task: {
        instruction: "Trage für dein Dokument einen Titel, einen Autor und mindestens ein Stichwort ein.",
        hint: "Datei → Informationen → rechts „Titel hinzufügen“, „Stichwörter hinzufügen“, Autor prüfen.",
        check: async () => {
          const eigenschaften = await readEigenschaften();
          if (!eigenschaften) {
            return unsupported(
              "Diese Word-Version stellt dem Add-in die Dokumenteigenschaften nicht zur Verfügung (ab WordApi 1.3).",
              "Prüfe unter Datei → Informationen selbst. Danach kannst du den Schritt selbst als erledigt markieren.",
            );
          }
          return fromCriteria(
            [
              criterion(`Titel: ${eigenschaften.title || "leer"}`, eigenschaften.title.length > 0),
              criterion(`Autor: ${eigenschaften.author || "leer"}`, eigenschaften.author.length > 0),
              criterion(`Stichwörter: ${eigenschaften.keywords || "leer"}`, eigenschaften.keywords.length > 0),
            ],
            {
              success: "Das Dokument weiß jetzt, wie es heißt – und PDF und Suche wissen es auch.",
              failure: "Es fehlen noch Eigenschaften.",
            },
          );
        },
      },
    },
    {
      id: "eigenschaft-als-feld",
      title: "Eine Eigenschaft als Feld",
      explanation: [
        "Eigenschaften lassen sich im Text anzeigen: „Einfügen“ → „Schnellbausteine“ → " +
          "„Dokumenteigenschaft“ → „Titel“. Es entsteht ein Steuerelement, das mit der " +
          "Eigenschaft verbunden ist – wer darin tippt, ändert den Titel des Dokuments, und " +
          "wer den Titel unter „Datei“ → „Informationen“ ändert, ändert das Steuerelement.",
        "Für Kopf- und Fußzeilen ist ein Feld oft praktischer: „Einfügen“ → " +
          "„Schnellbausteine“ → „Feld“ → „Title“. Es zeigt den Titel nur an und " +
          "aktualisiert sich mit F9 oder beim Drucken.",
        "Deckblätter aus dem Katalog nutzen genau diese Verbindung. Und in Vorlagen ist sie " +
          "Gold wert: Titel einmal eintragen, und Deckblatt, Kopfzeile und PDF stimmen.",
      ],
      keyPoints: [
        "Einfügen → Schnellbausteine → Dokumenteigenschaft (verbunden, beschreibbar).",
        "Einfügen → Schnellbausteine → Feld → Title (nur Anzeige).",
        "Ein Titel, viele Stellen – immer gleichlautend.",
      ],
      task: {
        instruction:
          "Zeige den Titel deines Dokuments im Text an – als verbundenes Steuerelement oder als Feld (auch in der Kopfzeile).",
        hint: "Einfügen → Schnellbausteine → Dokumenteigenschaft → Titel, oder Schnellbausteine → Feld → Title.",
        check: async () => {
          const [felder, steuerelemente] = await Promise.all([readFields(), readContentControls()]);
          const titelFeld = (felder ?? []).some((feld) => /^(TITLE|DOCPROPERTY\s+"?Title"?)\b/i.test(feld.code));
          const titelElement = steuerelemente.some((element) => /^(Titel|Title)$/i.test(element.title));
          if (felder === null && !titelElement) {
            return unsupported(
              "Diese Word-Version stellt dem Add-in keine Felder zur Verfügung (ab WordApi 1.4), " +
                "und im Haupttext steht kein Titel-Steuerelement.",
              "Ändere den Titel unter Datei → Informationen und schau, ob der Text mitwandert. Danach kannst du den Schritt selbst als erledigt markieren.",
            );
          }
          return fromCriteria(
            [criterion("Ein Feld oder Steuerelement zeigt den Titel an", titelFeld || titelElement)],
            {
              success: "Der Titel pflegt sich jetzt an allen Stellen selbst.",
              failure: "Es gibt noch kein Feld und kein Steuerelement für den Titel.",
              hint: "Ein getippter Titel zählt nicht – er ändert sich nicht mit der Eigenschaft.",
            },
          );
        },
      },
    },
  ],
};
